import React from "react";
import styled from "@emotion/styled";
import { Link } from "gatsby";
import { breakpoints, theme } from "../../utils/styles";
import { Global } from "@emotion/core";
import { Helmet } from "react-helmet";
import MarangaImage from "../../assets/MM_hiring cover photo.jpg";

const Page = styled.main({
  minHeight: '100vh',
  background: theme.white,
  color: theme.black,
  margin: 0,
  padding: 0,
  position: 'relative',
});

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 32px 64px;
  
  @media (max-width: ${breakpoints.l}px) {
    padding: 100px 24px 48px;
  }
`;

const Header = styled.header`
  margin-bottom: 48px;
  text-align: left;
`;

const Title = styled.h1`
  font-size: clamp(28px, 3.5vw, 40px);
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: ${theme.black};
  font-family: Nunito, sans-serif;
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 1rem;
  color: rgba(0,0,0,0.6);
  font-family: Nunito, sans-serif;
  margin-bottom: 32px;
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 48px;
  
  @media (max-width: ${breakpoints.l}px) {
    height: 300px;
    margin-bottom: 32px;
  }
`;

const Content = styled.div`
  font-family: Nunito, sans-serif;
  line-height: 1.7;
  font-size: 1.1rem;
  color: ${theme.black};
  
  p {
    margin: 0 0 24px 0;
  }
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 48px 0 24px 0;
    color: ${theme.black};
  }
  
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 32px 0 16px 0;
    color: ${theme.black};
  }
  
  blockquote {
    border-left: 4px solid ${theme.primary};
    margin: 32px 0;
    padding: 16px 24px;
    background: #f9f9f9;
    font-style: italic;
  }
  
  ul, ol {
    margin: 24px 0;
    padding-left: 24px;
  }
  
  li {
    margin-bottom: 8px;
  }
  
  .highlight {
    background: linear-gradient(120deg, ${theme.primary}20 0%, ${theme.primary}20 100%);
    padding: 2px 4px;
    border-radius: 3px;
  }
  
  a {
    color: ${theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${theme.primary};
  text-decoration: none;
  font-family: Nunito, sans-serif;
  font-weight: 600;
  margin-bottom: 32px;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:before {
    content: "←";
    font-size: 1.2rem;
  }
`;

export default function MarangaMokayaPost() {
  return (
    <Page>
      <Helmet>
        <title>Scindo strengthens ML team with Maranga Mokaya | Scindo</title>
        <meta name="description" content="Scindo welcomes Maranga Mokaya, Machine Learning Scientist from Oxford's Protein Informatics Group, bringing expertise in computational chemistry, protein informatics, and AI-driven molecular design." />
        
        {/* Open Graph tags for social media sharing */}
        <meta property="og:title" content="Scindo strengthens ML team with Maranga Mokaya" />
        <meta property="og:description" content="Scindo welcomes Maranga Mokaya, Machine Learning Scientist from Oxford's Protein Informatics Group, bringing expertise in computational chemistry and AI-driven molecular design." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.scindo.bio/blog/maranga-mokaya-joins-scindo" />
        <meta property="og:site_name" content="Scindo" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Scindo strengthens ML team with Maranga Mokaya" />
        <meta name="twitter:description" content="Scindo welcomes Maranga Mokaya, Machine Learning Scientist from Oxford's Protein Informatics Group." />
        
        {/* Additional SEO tags */}
        <meta name="author" content="Scindo" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.scindo.bio/blog/maranga-mokaya-joins-scindo" />
      </Helmet>
      
      <Global styles={{
        'html, body': {
          background: theme.white,
          color: theme.black,
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
      }} />
      
      <Container>
        <BackLink to="/media">Back to Media</BackLink>
        
        <Header>
          <Title>Scindo strengthens ML team with Maranga Mokaya</Title>
          <Meta>1 October 2025</Meta>
        </Header>
        
        <FeaturedImage 
          src={MarangaImage} 
          alt="Maranga Mokaya joins Scindo"
        />
        
        <Content>
          <p>
            In August, we were delighted to welcome <span className="highlight">Maranga Mokaya</span> to Scindo's computational team as a Machine Learning Scientist.
          </p>
          
          <p>
            Maranga brings a rare combination of computational chemistry, protein informatics, and advanced machine-learning expertise. He completed his PhD at the University of Oxford in Charlotte Deane's Oxford Protein Informatics Group (OPIG), where he worked with Exscientia (now part of Recursion) to develop AI models for the design, optimisation, and synthesis of new drug-like molecules.
          </p>
          
          <h2>Advancing AI for Molecular Design</h2>
          
          <p>
            In 2023, Maranga published a <a href="https://www.nature.com/articles/s42256-023-00636-2" target="_blank" rel="noopener noreferrer">first-author paper in <em>Nature Machine Intelligence</em></a>, introducing a curriculum-learning framework for deep reinforcement learning that improves the diversity and controllability of de novo molecular generation.
          </p>
          
          <p>
            His study showed that standard SMILES-based RNN models often struggle when very few examples of desired molecules exist in the training data, producing narrow and hard-to-control chemical outputs. Maranga's iterative curriculum approach overcame these limitations by allowing models to gradually learn increasingly complex molecular profiles — yielding up to 18× more unique scaffolds than conventional methods, while also demonstrating where one-dimensional representations fundamentally constrain optimisation success.
          </p>
          
          <h2>Contributing to Scindo's AI Platform</h2>
          
          <p>
            With experience spanning quantum chemistry, molecular modelling, and sequence- and structure-based ML, Maranga will be an important contributor to the development of Scindo's enzyme function prediction models, generative enzyme sequence design, and the expansion of our reaction–sequence datasets that underpin our active-learning workflows.
          </p>
          
          <p>
            Maranga holds an MChem from Durham University and previously developed machine-learning tools for protein structure prediction at the University of Nottingham. Outside the lab, he is also one of the UK's top 400 m hurdlers.
          </p>
          
          <p>
            We're delighted to have Maranga on board as we continue to integrate machine learning, chemistry, and biology to design enzymes that redefine how chemistry is made.
          </p>
        </Content>
      </Container>
    </Page>
  );
}
